<template>
  <div class="karaoke-player">
    <div v-if="surface !== 'sender'" class="player-header">
      <div class="player-queue-info">Songs in queue: {{ queue.length > 0 ? queue.length - 1 : 0 }}</div>
      <div class="player-room-id" @click="promptRoomId">Room ID: {{ roomId }}</div>
      <div class="player-logo" @click="confirmExit" role="button" tabindex="0">
        <span class="player-logo-icon">🎤✨</span>
        <span class="player-logo-text">mykaraoke.world</span>
      </div>
    </div>
    <div class="player-container">
      <div id="youtube-player"></div>
      <div v-if="!currentSong" class="empty-queue-message">
        <template v-if="isCastReceiver">
          <h2>How to Add Songs</h2>
          <div class="instructions-steps">
            <div class="instruction-step">
              <div class="step-number">1</div>
              <div>Open <span class="highlight">mykaraoke.world</span> on your phone or computer</div>
            </div>
            <div class="instruction-step">
              <div class="step-number">2</div>
              <div>Enter room ID: <span class="highlight">{{ roomId }}</span></div>
            </div>
            <div class="instruction-step">
              <div class="step-number">3</div>
              <div>Search for your favorite songs</div>
            </div>
            <div class="instruction-step">
              <div class="step-number">4</div>
              <div>Click "Add to Queue" to start singing!</div>
            </div>
          </div>
          <p class="instructions-tagline">Get ready to sing your heart out! 🎤✨</p>
        </template>
        <template v-else>
          <h2>No songs in queue</h2>
          <p>Reserve a song to start playing</p>
        </template>
      </div>
      <div v-if="isPaused" class="paused-overlay">
        <div class="paused-text">PAUSED</div>
      </div>
      <!-- Activity Feed -->
      <div class="activity-feed">
        <!-- Debug: Show count -->
        <div v-if="activities.length === 0" style="display: none;">No activities</div>
        <div 
          v-for="activity in activities" 
          :key="activity.id"
          class="activity-item"
          :style="{
            background: `rgba(0, 0, 0, 0.9)`,
            borderLeftColor: activity.userColor
          }"
        >
          <div class="user-badge" :style="{ background: activity.userColor }"></div>
          <span v-if="activity.isPremium" class="subscriber-badge-inline">
            <svg viewBox="0 0 24 24" fill="#ffa500" width="12" height="12">
              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
            </svg>
          </span>
          <span class="activity-text">{{ formatActivity(activity) }}</span>
        </div>
      </div>
    </div>
    <div class="youtube-attribution">
      <a href="https://www.youtube.com" target="_blank" class="youtube-link">
        <img src="https://www.youtube.com/img/desktop/yt_1200.png" alt="YouTube" class="youtube-logo">
      </a>
      <span class="attribution-text">Content provided by YouTube</span>
    </div>
  </div>
</template>

<script>
console.log('🎤 KaraokePlayer.vue is being loaded!');

import { initializeApp, getApps } from 'firebase/app'
import { getFirestore, collection, query, orderBy, onSnapshot, deleteDoc, doc, updateDoc, connectFirestoreEmulator } from 'firebase/firestore'
import { toRaw } from 'vue'

const firebaseConfig = {
  apiKey: "AIzaSyAXwf1z_HhrB5MKCf5aCnVUnUz89Uyz4A8",
  authDomain: "my-karaoke-world.firebaseapp.com",
  projectId: "my-karaoke-world",
  storageBucket: "my-karaoke-world.firebasestorage.app",
  messagingSenderId: "448846533841",
  appId: "1:448846533841:web:5df8ea2222e3f529f561f0",
  measurementId: "G-0HMXH8192Z"
}

let karaokeApp = null;
let karaokeDb = null;
let karaokeDbEmulatorConnected = false;

export default {
  name: 'KaraokePlayer',
  expose: ['playVideo', 'pauseVideo', 'player'],
  emits: ['queue-updated', 'player-ready', 'player-error', 'room-id-change'],
  props: {
    roomId: {
      type: String,
      required: true
    },
    isCastReceiver: {
      type: Boolean,
      default: false
    },
    isCasting: {
      type: Boolean,
      default: false
    },
    surface: {
      type: String,
      default: null,
      validator: v => v === null || v === 'sender' || v === 'receiver'
    },
    analyticsEmitter: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      player: null,
      currentSong: null,
      queue: [],
      unsubscribeQueue: null,
      unsubscribeControls: null,
      unsubscribeActivity: null,
      db: null,
      isQueueListenerSetup: false,
      lastPausedState: null,
      isPaused: false,
      isUpdatingPauseState: false,
      isLocalAction: false,
      activities: [],
      activityTimeouts: {}
    }
  },
  methods: {
    initYouTube() {
      console.group('Initializing YouTube Player');
      console.log('Current state:', {
        hasYT: typeof YT !== 'undefined',
        hasPlayer: !!this.player,
        isCastReceiver: this.isCastReceiver
      });

      if (typeof YT === 'undefined') {
        console.log('Loading YouTube IFrame API');
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        const firstScriptTag = document.getElementsByTagName('script')[0];
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
        window.onYouTubeIframeAPIReady = () => {
          console.log('YouTube IFrame API Ready');
          this.createPlayer();
        };
      } else {
        console.log('YouTube IFrame API already loaded');
        this.createPlayer();
      }
      console.groupEnd();
    },
    createPlayer() {
      console.group('Creating YouTube Player');
      console.log('Player config:', {
        isCastReceiver: this.isCastReceiver,
        elementExists: !!document.getElementById('youtube-player'),
        host: window.location.host
      });

      const playerVars = {
        autoplay: 1,
        controls: this.isCastReceiver ? 0 : 1,
        disablekb: this.isCastReceiver ? 1 : 0,
        fs: 0,
        rel: 0,
        playsinline: 1,
        modestbranding: 1,
        enablejsapi: 1,
        html5: 1,
        iv_load_policy: 3,
        origin: window.location.origin
      };

      if (this.isCastReceiver) {
        // Force specific settings for Chromecast
        playerVars.host = 'https://www.youtube.com';
        playerVars.widget_referrer = window.location.href;
      }

      console.log('Player variables:', playerVars);

      this.player = new YT.Player('youtube-player', {
        height: '100%',
        width: '100%',
        playerVars,
        events: {
          onReady: this.onPlayerReady,
          onStateChange: this.onPlayerStateChange,
          onError: this.onPlayerError
        }
      });
      console.groupEnd();
    },
    onPlayerReady(event) {
      console.group('YouTube Player Ready');
      console.log('Player instance:', {
        hasPlayer: !!this.player,
        playerState: this.player?.getPlayerState(),
        currentTime: this.player?.getCurrentTime()
      });
      this.$emit('player-ready', event);
      
      // Check if we have songs to play
      if (!this.currentSong && this.queue.length > 0) {
        console.log('Playing first song in queue');
        this.playNextSong();
      }
      console.groupEnd();
    },
    async onPlayerStateChange(event) {
      const stateNames = {
        '-1': 'UNSTARTED',
        '0': 'ENDED',
        '1': 'PLAYING',
        '2': 'PAUSED',
        '3': 'BUFFERING',
        '5': 'CUED'
      };
      console.log(`[${this.isCastReceiver ? 'RECEIVER' : 'SENDER'}] Player state changed:`, stateNames[event.data] || event.data, 'isLocalAction:', this.isLocalAction);
      this.$emit('state-change', event);

      // Sync pause/play state to Firestore
      // YT.PlayerState: -1 (unstarted), 0 (ended), 1 (playing), 2 (paused), 3 (buffering), 5 (cued)
      if (event.data === 1) { // PLAYING
        this.isPaused = false;
        // Only update Firestore if this was a local action (user initiated)
        // Don't update if this was triggered by Firestore listener (remote action)
        if (this.db && this.roomId && !this.isUpdatingPauseState && this.isLocalAction) {
          console.log(`[${this.isCastReceiver ? 'RECEIVER' : 'SENDER'}] Updating Firestore: isPaused = false`);
          try {
            this.isUpdatingPauseState = true;
            const roomRef = doc(this.db, 'rooms', this.roomId);
            await updateDoc(roomRef, { isPaused: false });
            // Update lastPausedState to prevent the listener from reacting
            this.lastPausedState = false;
          } catch (error) {
            console.error('Error updating isPaused to false:', error);
          } finally {
            this.isUpdatingPauseState = false;
          }
        }
        // Reset local action flag
        this.isLocalAction = false;
      } else if (event.data === 2) { // PAUSED
        this.isPaused = true;
        // Only update Firestore if this was a local action (user initiated)
        // Don't update if this was triggered by Firestore listener (remote action)
        if (this.db && this.roomId && !this.isUpdatingPauseState && this.isLocalAction) {
          console.log(`[${this.isCastReceiver ? 'RECEIVER' : 'SENDER'}] Updating Firestore: isPaused = true`);
          try {
            this.isUpdatingPauseState = true;
            const roomRef = doc(this.db, 'rooms', this.roomId);
            await updateDoc(roomRef, { isPaused: true });
            // Update lastPausedState to prevent the listener from reacting
            this.lastPausedState = true;
          } catch (error) {
            console.error('Error updating isPaused to true:', error);
          } finally {
            this.isUpdatingPauseState = false;
          }
        }
        // Reset local action flag
        this.isLocalAction = false;
      }

      // YT.PlayerState.ENDED = 0
      if (event.data === 0) {
        await this.removeCurrentSong();
        this.playNextSong();
      }
    },
    onPlayerError(event) {
      console.error('YouTube player error:', event);
      this.$emit('player-error', event);
      this.playNextSong();
    },
    initFirestore() {
      if (this.db) {
        console.log('Firestore already initialized, skipping');
        return;
      }
      try {
        console.group('Initializing Firestore');
        if (!karaokeApp) {
          const existingApp = getApps().find(app => app.name === 'karaoke-player');
          karaokeApp = existingApp || initializeApp(firebaseConfig, 'karaoke-player');
        }
        if (!karaokeDb) {
          karaokeDb = getFirestore(karaokeApp);
        }
        this.db = karaokeDb;

        // Determine if we should use emulator
        // Require both: local hostname AND the env var explicitly set to 'true'
        const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const useEmulator = isLocal && process.env.VUE_APP_USE_FIREBASE_EMULATOR === 'true';

        if (useEmulator && !this.isCastReceiver) {
          if (!karaokeDbEmulatorConnected) {
            console.log('Connecting to Firestore emulator...');
            try {
              connectFirestoreEmulator(this.db, 'localhost', 9101);
              karaokeDbEmulatorConnected = true;
              console.log('Connected to Firestore emulator');
            } catch (error) {
              if (error?.code === 'failed-precondition') {
                karaokeDbEmulatorConnected = true;
                console.warn('Firestore emulator already configured, continuing');
              } else {
                throw error;
              }
            }
          } else {
            console.log('Firestore emulator already connected, skipping');
          }
        } else {
          console.log('Using production Firestore');
        }

        console.log('Firestore initialized:', {
          dbType: this.db?.constructor?.name,
          isInitialized: !!this.db,
          useEmulator,
          isCastReceiver: this.isCastReceiver,
          roomId: this.roomId
        });
        console.groupEnd();
        
        // Only set up listeners if we have a roomId
        if (this.roomId && !this.isQueueListenerSetup) {
          console.log('Setting up listeners after Firestore init...');
          this.setupQueueListener();
          this.setupPlaybackListener();
          this.setupActivityListener();
          this.isQueueListenerSetup = true;
        } else if (!this.roomId) {
          console.log('No roomId yet, will set up listeners when roomId is provided');
        }
      } catch (error) {
        console.error('Error initializing Firestore:', error);
        console.error('Error details:', {
          code: error.code,
          message: error.message,
          stack: error.stack
        });
      }
    },
    setupPlaybackListener() {
      console.log(`[${this.isCastReceiver ? 'RECEIVER' : 'SENDER'}] setupPlaybackListener called`, {
        hasRoomId: !!this.roomId,
        hasDb: !!this.db,
        roomId: this.roomId,
        isCastReceiver: this.isCastReceiver
      });
      
      if (!this.roomId || !this.db) {
        console.error(`[${this.isCastReceiver ? 'RECEIVER' : 'SENDER'}] Required props/state not available for playback listener`, {
          roomId: this.roomId,
          hasDb: !!this.db
        });
        return;
      }

      // Clean up any existing listener
      if (this.unsubscribeControls) {
        console.log('Cleaning up existing playback listener');
        this.unsubscribeControls();
        this.unsubscribeControls = null;
      }

      try {
        console.log(`[${this.isCastReceiver ? 'RECEIVER' : 'SENDER'}] Setting up playback listener for room:`, this.roomId);
        const roomRef = doc(this.db, 'rooms', this.roomId);

        this.unsubscribeControls = onSnapshot(roomRef, (snapshot) => {
          console.log(`[${this.isCastReceiver ? 'RECEIVER' : 'SENDER'}] 🔔 Playback listener triggered`);

          if (!snapshot.exists()) {
            console.log('Room snapshot does not exist');
            return;
          }
          
          if (!this.player) {
            console.log('Player not ready yet');
            return;
          }

          const data = snapshot.data();
          const playerState = this.player.getPlayerState ? this.player.getPlayerState() : null;
          const currentPausedState = data.isPaused;
          
          console.log(`[${this.isCastReceiver ? 'RECEIVER' : 'SENDER'}] Room data received:`, {
            isPaused: currentPausedState,
            lastPausedState: this.lastPausedState,
            hasPlayer: !!this.player,
            playerState: playerState
          });
          
          // Skip undefined values (field doesn't exist yet)
          if (currentPausedState === undefined) {
            console.log('isPaused is undefined, skipping');
            return;
          }
          
          // Update the isPaused state for UI
          this.isPaused = currentPausedState;
          
          // Check if state changed
          if (currentPausedState !== this.lastPausedState) {
            console.log('isPaused state changed from', this.lastPausedState, 'to', currentPausedState);
            this.lastPausedState = currentPausedState;
            
            if (currentPausedState === true) {
              console.log('Room is paused, checking if we need to pause player');
              console.log('Current player state:', playerState);
              if (playerState === 1) { // 1 = PLAYING
                console.log('Player is playing, calling pauseVideo() from Firestore listener');
                this.pauseVideo(false); // Pass false to indicate this is NOT a local action
              } else {
                console.log('Player is not playing, no action needed');
              }
            } else if (currentPausedState === false) {
              console.log('Room should be playing, checking if we need to resume player');
              console.log('Current player state:', playerState);
              if (playerState === 2) { // 2 = PAUSED
                console.log('Player is paused, calling playVideo() from Firestore listener');
                this.playVideo(false); // Pass false to indicate this is NOT a local action
              } else {
                console.log('Player is not paused, no action needed');
              }
            }
          } else {
            console.log('No change in isPaused state (both are', currentPausedState, '), ignoring');
          }
        }, (error) => {
          console.error('Error listening to playback state:', error);
        });

        console.log('Playback listener setup complete');
      } catch (error) {
        console.error('Error setting up playback listener:', error);
      }
    },
    setupActivityListener() {
      if (!this.roomId || !this.db) {
        console.error('Required props/state not available for activity listener');
        return;
      }

      // Clean up any existing listener
      if (this.unsubscribeActivity) {
        this.unsubscribeActivity();
        this.unsubscribeActivity = null;
      }

      try {
        console.log('Setting up activity listener for room:', this.roomId);
        const activityRef = collection(this.db, 'rooms', this.roomId, 'activity');
        const activityQuery = query(activityRef, orderBy('timestamp', 'desc'));

        let isFirstSnapshot = true;

        this.unsubscribeActivity = onSnapshot(activityQuery, (snapshot) => {
          // Skip initial snapshot to avoid showing old activities
          if (isFirstSnapshot) {
            console.log('Skipping initial activity snapshot (contains old activities)');
            isFirstSnapshot = false;
            return;
          }

          snapshot.docChanges().forEach((change) => {
            if (change.type === 'added') {
              const activityData = change.doc.data();
              
              // Only show activities that are less than 10 seconds old
              // (to handle race conditions and reconnections)
              const activityTime = activityData.timestamp?.toMillis() || 0;
              const now = Date.now();
              const ageInSeconds = (now - activityTime) / 1000;
              
              if (ageInSeconds > 10) {
                console.log('Skipping old activity (age:', ageInSeconds, 'seconds)');
                return;
              }

              const activity = {
                id: change.doc.id,
                ...activityData
              };

              console.log('✨ NEW ACTIVITY:', activity.action, 'by', activity.userName);
              console.log('Activity data:', activity);
              console.log('Activities array before add:', this.activities.length);

              // Add to activities array (prepend to show at top)
              this.activities.unshift(activity);
              
              console.log('Activities array after add:', this.activities.length);
              console.log('Current activities:', this.activities.map(a => a.action));

              // Limit to 5 activities
              if (this.activities.length > 5) {
                const removed = this.activities.pop();
                // Clear timeout for removed activity
                if (this.activityTimeouts[removed.id]) {
                  clearTimeout(this.activityTimeouts[removed.id]);
                  delete this.activityTimeouts[removed.id];
                }
              }

              // Auto-remove after 5 seconds
              this.activityTimeouts[activity.id] = setTimeout(() => {
                const index = this.activities.findIndex(a => a.id === activity.id);
                if (index !== -1) {
                  console.log('Auto-removing activity:', activity.action);
                  this.activities.splice(index, 1);
                }
                delete this.activityTimeouts[activity.id];
              }, 5000);
            }
          });
        }, (error) => {
          console.error('Error listening to activity:', error);
        });

        console.log('Activity listener setup complete');
      } catch (error) {
        console.error('Error setting up activity listener:', error);
      }
    },
    formatActivity(activity) {
      const userName = activity.userName || 'Anonymous';
      switch (activity.action) {
        case 'song_added':
          return `🎵 ${userName} added ${activity.songTitle || 'a song'}`;
        case 'paused':
          return `⏸ ${userName} paused playback`;
        case 'resumed':
          return `▶️ ${userName} resumed playback`;
        case 'skipped':
          return `⏭ ${userName} skipped to next song`;
        case 'queue_reordered':
          return `🔄 ${userName} reordered the queue`;
        default:
          return `${userName} performed an action`;
      }
    },
    setupQueueListener() {
      if (!this.roomId || !this.db) {
        console.error('Required props/state not available:', {
          roomId: this.roomId,
          hasDb: !!this.db
        });
        return;
      }

      // Clean up any existing listener
      if (this.unsubscribeQueue) {
        console.log('Cleaning up existing queue listener');
        this.unsubscribeQueue();
        this.unsubscribeQueue = null;
      }

      try {
        console.group('Setting up queue listener');
        console.log('Component props:', {
          roomId: this.roomId,
          isCastReceiver: this.isCastReceiver,
          hasDb: !!this.db,
          dbType: this.db?.constructor?.name
        });

        const queueRef = collection(this.db, 'rooms', this.roomId, 'queue');
        console.log('Queue collection path:', `rooms/${this.roomId}/queue`);

        const queueQuery = query(queueRef, orderBy('position', 'asc'));

        this.unsubscribeQueue = onSnapshot(queueQuery, (snapshot) => {
          console.group('Queue update received');
          console.log('Snapshot details:', {
            size: snapshot.size,
            empty: snapshot.empty,
            metadata: {
              hasPendingWrites: snapshot.metadata.hasPendingWrites,
              fromCache: snapshot.metadata.fromCache
            }
          });
          
          // Log each document in the snapshot
          snapshot.docs.forEach(doc => {
            console.log('Queue item:', {
              id: doc.id,
              data: doc.data(),
              exists: doc.exists()
            });
          });
          
          // Update queue with more detailed logging
          const oldQueue = [...this.queue];
          this.queue = snapshot.docs.map(doc => {
            const data = doc.data();
            const timestamp = data.timestamp?.toMillis?.() || Date.now();
            console.log('Processing queue item:', {
              id: doc.id,
              videoId: data.videoId,
              timestamp
            });
            return {
              id: doc.id,
              ...data,
              timestamp
            };
          });
          
          console.log('Queue update:', {
            oldLength: oldQueue.length,
            newLength: this.queue.length,
            oldFirstSong: oldQueue[0]?.videoId,
            newFirstSong: this.queue[0]?.videoId,
            currentSong: this.currentSong?.videoId,
            hasPlayer: !!this.player,
            playerState: this.player?.getPlayerState?.()
          });
          
          // Emit queue update event for parent components
          console.log('📊 Emitting queue-updated event:', { queueLength: this.queue.length, queue: this.queue });
          this.$emit('queue-updated', this.queue);

          // Handle current song removal
          if (this.currentSong) {
            const currentSongStillInQueue = this.queue.some(song => song.id === this.currentSong.id);
            if (!currentSongStillInQueue) {
              console.log('Current song was removed from queue');
              this.currentSong = null;
              this.playNextSong();
            }
          }
          
          // Start playing if we have songs but nothing is playing
          if (!this.currentSong && this.queue.length > 0 && this.player?.getPlayerState) {
            const playerState = this.player.getPlayerState();
            console.log('No current song playing, queue has songs:', {
              queueLength: this.queue.length,
              playerState,
              firstSong: this.queue[0]
            });
            this.playNextSong();
          }
          
          console.groupEnd();
        }, (error) => {
          console.error('Error listening to queue:', error);
          console.error('Error details:', {
            code: error.code,
            message: error.message,
            stack: error.stack
          });
        });
        
        console.log('Queue listener setup complete');
        console.groupEnd();
      } catch (error) {
        console.error('Error setting up queue listener:', error);
        console.error('Stack:', error.stack);
      }
    },
    async removeCurrentSong() {
      if (!this.currentSong || !this.db) return;
      
      try {
        const songRef = doc(this.db, 'rooms', this.roomId, 'queue', this.currentSong.id);
        await deleteDoc(songRef);
        this.currentSong = null;
      } catch (error) {
        console.error('Error removing song from queue:', error);
      }
    },
    playNextSong() {
      console.group('Playing next song');
      console.log('Current state:', {
        queueLength: this.queue.length,
        hasPlayer: !!this.player,
        currentSong: this.currentSong?.videoId,
        playerState: this.player?.getPlayerState?.()
      });

      if (this.queue.length === 0) {
        console.log('No songs in queue');
        this.currentSong = null;
        console.groupEnd();
        return;
      }

      this.currentSong = this.queue[0];
      console.log('Selected song to play:', {
        id: this.currentSong.id,
        videoId: this.currentSong.videoId,
        timestamp: this.currentSong.timestamp
      });

      if (this.player && this.currentSong) {
        console.log('Loading video:', this.currentSong.videoId);
        this.isLocalAction = true;
        this.player.loadVideoById({
          videoId: this.currentSong.videoId,
          startSeconds: 0
        });
        this.player.playVideo();
        // Clear paused state when starting a new song
        this.isPaused = false;

        // Track song started with surface info
        if (this.analyticsEmitter) {
          try {
            this.analyticsEmitter('song_playback_started', {
              platform: this.detectPlatform(),
              surface: this.surface,
              room_id: this.roomId,
              video_id: this.currentSong.videoId,
              title: this.currentSong.title,
              added_by: this.currentSong.addedBy || 'unknown'
            });
          } catch (e) {
            console.warn('song_playback_started emit failed:', e);
          }
        }
      } else {
        console.warn('Cannot play: player or currentSong not available', {
          hasPlayer: !!this.player,
          hasSong: !!this.currentSong
        });
      }
      console.groupEnd();
    },
    playVideo(isLocalAction = true) {
      if (this.player) {
        // Mark whether this is a local user action or remote listener action
        this.isLocalAction = isLocalAction;
        this.player.playVideo();
        this.isPaused = false;
      }
    },
    pauseVideo(isLocalAction = true) {
      if (this.player) {
        // Mark whether this is a local user action or remote listener action
        this.isLocalAction = isLocalAction;
        this.player.pauseVideo();
        this.isPaused = true;
      }
    },
    detectPlatform() {
      if (this.isCastReceiver) return 'cast';
      const ua = navigator.userAgent || '';
      if (/Mobi|Android|iPhone|iPad|iPod/i.test(ua)) return 'mobile-browser';
      return 'desktop-browser';
    },
    promptRoomId() {
      const newId = prompt('Enter Room ID:', this.roomId);
      if (newId && newId !== this.roomId) {
        this.$emit('room-id-change', newId);
      }
    },
    confirmExit() {
      if (confirm('Are you sure you want to exit the Karaoke room?')) {
        window.location.href = '/';
      }
    }
  },
  watch: {
    roomId: {
      handler(newId, oldId) {
        console.group(`[${this.isCastReceiver ? 'RECEIVER' : 'SENDER'}] Room ID changed in KaraokePlayer`);
        console.log('Old ID:', oldId);
        console.log('New ID:', newId);
        console.log('Has DB:', !!this.db);
        console.log('Listeners setup:', this.isQueueListenerSetup);
        
        if (newId && newId !== oldId) {
          // If we have a roomId but no db yet, initialize Firestore first
          if (!this.db) {
            console.log('DB not initialized yet, initializing now...');
            this.initFirestore();
          } else if (!this.isQueueListenerSetup) {
            // DB exists but listeners not set up yet, set them up now
            console.log('DB exists, setting up listeners...');
            this.setupQueueListener();
            this.setupPlaybackListener();
            this.setupActivityListener();
            this.isQueueListenerSetup = true;
          } else {
            // Listeners already set up, just clean up and re-setup for new room
            console.log('Listeners already setup, re-initializing for new room...');
            if (this.unsubscribeQueue) this.unsubscribeQueue();
            if (this.unsubscribeControls) this.unsubscribeControls();
            if (this.unsubscribeActivity) this.unsubscribeActivity();
            
            this.setupQueueListener();
            this.setupPlaybackListener();
            this.setupActivityListener();
          }
        }
        console.groupEnd();
      },
      immediate: true
    }
  },
  mounted() {
    console.log(`[${this.isCastReceiver ? 'RECEIVER' : 'SENDER'}] KaraokePlayer mounted with props:`, {
      roomId: this.roomId,
      isCastReceiver: this.isCastReceiver
    });
    console.log('Available methods:', {
      hasSetupPlaybackListener: typeof this.setupPlaybackListener === 'function',
      hasSetupQueueListener: typeof this.setupQueueListener === 'function'
    });
    
    // Always initialize Firestore, even if roomId is null
    // Listeners will be set up when roomId becomes available
    this.initFirestore();
    this.initYouTube();
  },
  beforeUnmount() {
    if (this.unsubscribeQueue) {
      this.unsubscribeQueue();
    }
    if (this.unsubscribeControls) {
      this.unsubscribeControls();
    }
    if (this.unsubscribeActivity) {
      this.unsubscribeActivity();
    }
    // Clear all activity timeouts
    Object.values(this.activityTimeouts).forEach(timeout => clearTimeout(timeout));
    if (this.player) {
      this.player.destroy();
    }
  }
}
</script>

<style>
.karaoke-player {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.player-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(255, 255, 255, 0.1);
  padding: 0.75rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
}

.player-logo {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: opacity 0.2s;
}

.player-logo:hover {
  opacity: 0.8;
}

.player-logo-icon {
  font-size: 1.25rem;
  color: #ffa500;
}

.player-logo-text {
  font-size: 1.25rem;
  color: #ffa500;
  font-weight: 500;
}

.player-queue-info {
  color: white;
  font-size: 0.875rem;
}

.player-room-id {
  color: #ffa500;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.player-container {
  flex: 1;
  position: relative;
  min-height: 0;
}

#youtube-player {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.empty-queue-message {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: white;
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
  z-index: 100;
  padding: 4rem;
}

.empty-queue-message h2 {
  margin: 0 0 2rem 0;
  font-size: 3.5rem;
  color: #ffa500;
  font-weight: 500;
}

.empty-queue-message p {
  margin: 0;
  font-size: 2rem;
  opacity: 0.9;
  line-height: 1.4;
}

.instructions-steps {
  margin: 2rem 0;
  text-align: left;
  max-width: 600px;
}

.instruction-step {
  display: flex;
  align-items: center;
  margin: 1rem 0;
  color: white;
  font-size: 1.25rem;
}

.step-number {
  background: #ffa500;
  color: black;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  font-weight: bold;
  flex-shrink: 0;
}

.highlight {
  color: #ffa500;
  font-weight: 500;
}

.instructions-tagline {
  margin-top: 2rem;
  font-size: 1.5rem;
  color: #ffa500;
  font-style: italic;
}

.paused-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.7);
  z-index: 200;
  backdrop-filter: blur(4px);
  animation: fadeIn 0.3s ease-in-out;
  pointer-events: none;
}

.paused-text {
  font-size: 5rem;
  font-weight: bold;
  color: white;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.8);
  letter-spacing: 0.5rem;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.activity-feed {
  position: absolute;
  bottom: 20px;
  right: 20px;
  z-index: 300;
  display: flex;
  flex-direction: column;
  gap: 8px;
  pointer-events: none;
  max-width: 350px;
}

.activity-item {
  padding: 10px 14px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  animation: slideIn 0.3s ease-out;
  font-size: 14px;
  color: white;
  border-left: 3px solid;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
}

.user-badge {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.3);
}

.activity-text {
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  line-height: 1.4;
}

.subscriber-badge-inline {
  display: inline-flex;
  align-items: center;
  margin-right: 4px;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.youtube-attribution {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.25rem;
  background: rgba(255, 255, 255, 0.1);
  padding: 0.4rem 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
}

.youtube-logo {
  height: 16px;
  width: auto;
}

.youtube-link {
  display: flex;
  align-items: center;
  text-decoration: none;
}

.attribution-text {
  color: white;
  font-size: 0.75rem;
  opacity: 0.8;
}

/* Mobile styles */
@media (max-width: 768px) {
  .player-header {
    padding: 0.4rem 0.75rem;
    gap: 0.25rem;
  }

  .player-queue-info {
    font-size: 0.75rem;
  }

  .player-room-id {
    font-size: 0.8rem;
  }

  .player-logo-icon,
  .player-logo-text {
    font-size: 0.9rem;
  }

  .empty-queue-message {
    padding: 1rem;
  }

  .empty-queue-message h2 {
    font-size: 1.5rem;
    margin: 0 0 0.5rem 0;
  }

  .empty-queue-message p {
    font-size: 1rem;
  }

  .youtube-attribution {
    padding: 0.25rem 0.5rem;
  }
}
</style>
